import axios from "axios";
import * as Papa from "papaparse";

import { sectionToTabs } from "./Constants";

/**
 * Get the list of tabs for the selected section result section
 * @returns String array of tabs for the current selected section
 */
export const getTabOptions = (selectedSection, numbOfTopVolcanoSamples) => {
  let options = sectionToTabs[selectedSection];

  // Heatmap's first tab depends on param user enters so update placeholder text
  if (selectedSection === "Heatmap") {
    const topX = options[0].replace(
      "<-numbOfTopVolcanoSamples->",
      numbOfTopVolcanoSamples
    );

    options[0] = topX;
  }

  return options;
};

/**
 * Fet the relevant style needed for the image depending on selected section
 * @param {string} selectedItem Current section selected by user
 */
export const getImageStyle = (selectedItem) => {
  if (selectedItem === "Venn-Diagram") {
    return {
      maxWidth: "600px",
      maxHeight: "600px",
      width: "100%",
      height: "auto",
    };
  }

  return { maxWidth: "100%", maxHeight: "100%", height: "auto" };
};

/**
 * Returns the data file & the download link for the file
 * @param {*} jobId Id of analysis submission job, used to help locate s3 file
 * @param {*} fileName Name of the s3 file
 * @returns {Object} Object containing parsed TSV data, download URL
 */
export const fetchDataFile = async (jobId, fileName) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_ENDPOINT}/api/s3Download?s3Path=${jobId}&fileName=${fileName}`
    );

    const dataText = await axios.get(response.data.url).then((res) => res.data);

    return {
      data: Papa.parse(
        // Files with ID at the start have a duplicate, unnamed ID column so we add a blank header at the beginning
        dataText.startsWith('"ID"\t') || dataText.startsWith('"category"\t')
          ? '" "'.concat("\t", dataText)
          : dataText,
        {
          header: true,
          skipEmptyLines: true,
          transformHeader: (header, index) => {
            if (index === 0 && (header === "" || header === "rn")) {
              // PCA files contain Sample instead of Proteins unlike all other files
              let header = fileName.startsWith("pca") ? "Sample" : "Protein";

              if (fileName.startsWith("randomforest_confusion"))
                header = "Group";

              return header;
            } else {
              return header;
            }
          },
        }
      ).data,
      downloadUrl: response.data.url,
      originalData: dataText, // Return the original data text for debugging
    };
  } catch (error) {
    console.error("Error fetching data file:", fileName);
    return { data: null, downloadUrl: null };
  }
};

export const fetchData = async (url) => {
  try {
    let csv = await axios.get(url).then((res) => res.data);

    return Papa.parse(csv, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header, index) => {
        if (index === 0 && (header === "" || header === "rn")) {
          return url.includes("pca_") ? "Sample" : "Protein";
        } else {
          return header;
        }
      },
    }).data;
  } catch (error) {
    console.error("Error fetching csv:", error);
  }
};

/**
 * Download the specified file from the submission
 * @param {string} jobId Id of analysis submission job
 * @param {string} fileName Name of file from the submission to download
 */
export const handleDownload = async (jobId, fileName) => {
  try {
    let file = fileName;

    // Handle random forest download, some tab contains multiple files first one is the image
    if (typeof file === "object") file = file[0];

    const res = await axios.get(
      `${process.env.REACT_APP_API_ENDPOINT}/api/s3Download?s3Path=${jobId}&fileName=${fileName}`
    );
    const link = document.createElement("a");
    link.href = res.data.url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

/**
 * Get s3 link for a file from the submission
 * @param {string} jobId Id of analysis submission job
 * @param {string} fileName Name of file to get file url
 * @returns
 */
export const getFileUrl = async (jobId, fileName) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_ENDPOINT}/api/s3Download?s3Path=${jobId}&fileName=${fileName}`
    );
    return response.data.url;
  } catch (error) {
    console.error("Error getting file url:", error);
  }
};

export const fetchImage = async (jobId, fileName) => {
  try {
    let response = await axios.get(
      `${process.env.REACT_APP_API_ENDPOINT}/api/s3Download?s3Path=${jobId}&fileName=${fileName}`
    );
    return response.data.url;
  } catch (error) {
    console.error("Error downloading image:", error);
  }
};

// D3 UTILS

export function createLegend(
  selection,
  legendDict,
  offsetX,
  offsetY,
  fontSize = 12,
  lineSpacing = 35
) {
  const legend = selection
    .append("g")
    .attr("class", "legend")
    .attr("transform", `translate(${offsetX}, ${offsetY})`);

  const legendItems = Object.keys(legendDict).map((key) => ({
    key,
    label: legendDict[key][0],
    color: legendDict[key][1],
  }));

  const legendItem = legend
    .selectAll(".legend-item")
    .data(legendItems)
    .enter()
    .append("g")
    .attr("class", "legend-item")
    .attr("transform", (d, i) => `translate(0,${i * lineSpacing})`);

  legendItem
    .append("circle")
    .attr("cx", 5)
    .attr("cy", 5)
    .attr("r", 8)
    .attr("fill", (d) => d.color);

  legendItem
    .append("text")
    .attr("x", 15)
    .attr("y", 5)
    .style("font-size", `${fontSize}px`)
    .attr("transform", `translate(8, 6)`)
    .text((d) => d.label);
}
